import type { DateTime } from "luxon";
import { getStaffingDate, isInPTO } from "../../utils/dateUtils";
import { deriveNurseStatusAtDate, getNurseStatusEndDate } from "../../utils/formatters";
import {
	type BaseNurseShift,
	EffectiveEmployeeStatus,
	type Nurse,
	type OutreachExcludedGroup,
	type OutreachExcludedPerson,
	OutreachExclusionFilter,
	OutreachExclusionReason,
	type ShiftRequest,
	type WeekData,
} from "../../utils/models";
import { requestTakesAnyRole, storedFormsOfRoles } from "../../utils/roles";
import { collectNurseShiftsAroundDay, SHIFT_OVERLAP_TOLERANCE_MS } from "../../utils/shiftAvailability";
import { isAgencyNurse } from "../../utils/warningDialogs/utils";

/**
 * DEMO ONLY (APP-933): who outreach would reach for a request, read from the department's real roster and schedule —
 * the request's role or job, charge-only, status, PTO, the shifts they already work. Only what has no client-side
 * source (how people answer offers) is seeded, in `demoOutreachSeed`.
 */

/** Hours in a day past which another shift would be daily overtime (the demo assumes the common 12-hour rule). */
const DAILY_OVERTIME_HOURS = 12;
/** Weekly hours past which a shift is overtime. */
export const WEEKLY_OVERTIME_HOURS = 40;
/** How far back "worked these hours" looks. */
export const WORK_HISTORY_DAYS = 30;
const MS_PER_HOUR = 60 * 60 * 1000;

export type RosterContext = {
	request: ShiftRequest;
	/** Active in the department, sorted by id. */
	roster: Nurse[];
	departmentId: string;
	dayStart: string;
	graceMinutes: number;
	jobManagementEnabled: boolean;
	/** The department's long-day limit. */
	longShiftHours: number;
	weeks: readonly WeekData[];
	/** Where the shift's staffing day sits in the loaded weeks; `null` when it isn't loaded, so shift checks can't run. */
	loadedDay: { weekIndex: number; dayNo: number } | null;
	nameOf: (nurseId: number) => string;
};

/** What the schedule says about a nurse, for scoring and the approval check; `null` where the shift's week isn't loaded. */
export type NurseFacts = {
	/** Scheduled hours in the shift's whole week, not counting this shift (later days included: they still make it overtime). */
	weekHours: number | null;
	/** Shifts with these exact hours in the last 30 loaded days. */
	sameHoursCount: number | null;
	/** Another shift overlapping this one. */
	hasConflict: boolean | null;
};

export type RosterSplit = { eligible: Nurse[]; groups: OutreachExcludedGroup[]; facts: ReadonlyMap<number, NurseFacts> };

const hoursOf = (shift: Pick<BaseNurseShift, "startTime" | "endTime">): number => (shift.endTime.toMillis() - shift.startTime.toMillis()) / MS_PER_HOUR;

const overlapMs = (a: { start: DateTime; end: DateTime }, b: BaseNurseShift): number =>
	Math.max(0, Math.min(a.end.toMillis(), b.endTime.toMillis()) - Math.max(a.start.toMillis(), b.startTime.toMillis()));

/** The request's own rule for who may take it: its job where the department schedules by job, else its roles; charge-only takes charge staff. */
const takesRequest = (nurse: Nurse, ctx: RosterContext): boolean => {
	const { request } = ctx;
	if (request.charge_only && !(nurse.canBeReliefCharge || nurse.isShiftManager)) return false;
	if (ctx.jobManagementEnabled && request.job_id) return nurse.jobIds?.includes(request.job_id) === true;
	// A role-mode request naming no role restricts no role (see `rolesTakingRequest`).
	return request.message.roles.length === 0 || requestTakesAnyRole(request.message.roles, storedFormsOfRoles([nurse.role]));
};

const nurseShiftsOf = (week: WeekData, nurseId: number): BaseNurseShift[] =>
	week.weeklyShifts.flatMap((shift) => shift.days.flatMap((day) => day.nurses.filter((row) => row.nurseId === nurseId && !row.isPendingToDelete)));

/** Same clock hours as the request: "worked these hours". */
const sameHours = (shift: BaseNurseShift, start: DateTime, end: DateTime): boolean =>
	shift.startTime.hour === start.hour && shift.startTime.minute === start.minute && shift.endTime.hour === end.hour && shift.endTime.minute === end.minute;

const factsOf = (nurse: Nurse, ctx: RosterContext, staffingDate: DateTime, aroundDay: readonly BaseNurseShift[]): NurseFacts => {
	const { loadedDay, request } = ctx;
	if (loadedDay === null) return { weekHours: null, sameHoursCount: null, hasConflict: null };
	const window = { start: request.message.shift_start, end: request.message.shift_end };
	const hasConflict = aroundDay.some((shift) => overlapMs(window, shift) > SHIFT_OVERLAP_TOLERANCE_MS.scheduled);
	const weekHours = nurseShiftsOf(ctx.weeks[loadedDay.weekIndex], nurse.id).reduce((sum, shift) => sum + hoursOf(shift), 0);
	const historyFrom = staffingDate.minus({ days: WORK_HISTORY_DAYS });
	const sameHoursCount = ctx.weeks
		.flatMap((week) => nurseShiftsOf(week, nurse.id))
		.filter((shift) => shift.startTime >= historyFrom && shift.startTime < window.start && sameHours(shift, window.start, window.end)).length;
	return { weekHours, sameHoursCount, hasConflict };
};

const named = (nurse: Nurse, ctx: RosterContext): Pick<OutreachExcludedPerson, "nurseId" | "name"> => ({ nurseId: nurse.id, name: ctx.nameOf(nurse.id) });

type Exclusion = { filter: OutreachExclusionFilter; person: OutreachExcludedPerson };

/** Why the shift can't go to this nurse, most permanent first, or `null` when it can. */
const exclusionOf = (nurse: Nurse, ctx: RosterContext, staffingDate: DateTime, aroundDay: readonly BaseNurseShift[]): Exclusion | null => {
	const { request } = ctx;
	const window = { start: request.message.shift_start, end: request.message.shift_end };
	if (!takesRequest(nurse, ctx))
		return { filter: OutreachExclusionFilter.DifferentRole, person: { ...named(nurse, ctx), reason: OutreachExclusionReason.Role, role: nurse.role } };
	if (!request.charge_only && nurse.isShiftManager)
		return { filter: OutreachExclusionFilter.ChargeNurse, person: { ...named(nurse, ctx), reason: OutreachExclusionReason.ChargeNurse } };
	const leaves = nurse.scheduledDeactivationDate;
	if (leaves && leaves < staffingDate.startOf("day")) {
		return { filter: OutreachExclusionFilter.InactiveOrLeaving, person: { ...named(nurse, ctx), reason: OutreachExclusionReason.LastDay, date: leaves } };
	}
	const status = deriveNurseStatusAtDate(nurse, ctx.departmentId, staffingDate);
	if (status === EffectiveEmployeeStatus.OnLeave || status === EffectiveEmployeeStatus.Suspended) {
		const reason = status === EffectiveEmployeeStatus.OnLeave ? OutreachExclusionReason.OnLeave : OutreachExclusionReason.Suspended;
		return { filter: OutreachExclusionFilter.InactiveOrLeaving, person: { ...named(nurse, ctx), reason, to: getNurseStatusEndDate(nurse, ctx.departmentId) } };
	}
	const pto = nurse.employeeSpecificPreferences.find((pref) => isInPTO(pref, window.start, ctx.dayStart, window.end));
	if (pto !== undefined)
		return {
			filter: OutreachExclusionFilter.Pto,
			person: { ...named(nurse, ctx), reason: OutreachExclusionReason.Pto, from: pto.start_date, to: pto.end_date },
		};
	const overlapping = aroundDay.find((shift) => overlapMs(window, shift) > SHIFT_OVERLAP_TOLERANCE_MS.scheduled);
	if (overlapping !== undefined) {
		return {
			filter: OutreachExclusionFilter.OverlappingShift,
			person: { ...named(nurse, ctx), reason: OutreachExclusionReason.WorkingShift, from: overlapping.startTime, to: overlapping.endTime },
		};
	}
	const sameDay = aroundDay.filter((shift) => getStaffingDate(shift.startTime, ctx.dayStart, ctx.graceMinutes).hasSame(staffingDate, "day"));
	const dayHours = sameDay.reduce((sum, shift) => sum + hoursOf(shift), hoursOf({ startTime: window.start, endTime: window.end }));
	const other = sameDay[0];
	if (other === undefined) return null;
	const working: OutreachExcludedPerson = { ...named(nurse, ctx), reason: OutreachExclusionReason.WorkingThatDay, from: other.startTime, to: other.endTime };
	if (dayHours > ctx.longShiftHours) return { filter: OutreachExclusionFilter.OverMaxDailyHours, person: working };
	if (dayHours > DAILY_OVERTIME_HOURS) return { filter: OutreachExclusionFilter.DailyOvertime, person: working };
	return null;
};

const FILTER_ORDER: readonly OutreachExclusionFilter[] = [
	OutreachExclusionFilter.ChargeNurse,
	OutreachExclusionFilter.DifferentRole,
	OutreachExclusionFilter.InactiveOrLeaving,
	OutreachExclusionFilter.Pto,
	OutreachExclusionFilter.OverlappingShift,
	OutreachExclusionFilter.OverMaxDailyHours,
	OutreachExclusionFilter.DailyOvertime,
];

/**
 * The roster split the way outreach would: who it can ask, and who it leaves out and why. Agency staff aren't the
 * department's own staff, so they're in neither list.
 */
export const splitRoster = (ctx: RosterContext): RosterSplit => {
	const staffingDate = getStaffingDate(ctx.request.message.shift_start, ctx.dayStart, ctx.graceMinutes);
	const eligible: Nurse[] = [];
	const byFilter = new Map<OutreachExclusionFilter, OutreachExcludedPerson[]>();
	const facts = new Map<number, NurseFacts>();
	for (const nurse of ctx.roster) {
		if (isAgencyNurse(nurse.employmentType)) continue;
		// DEFAULT: with the shift's week not loaded there are no shifts to read, so the overlapping-shift and daily-hours
		// exclusions are skipped and `factsOf` reports the schedule facts as unknown.
		const aroundDay =
			ctx.loadedDay === null
				? []
				: collectNurseShiftsAroundDay({
						weeks: ctx.weeks,
						nurseId: nurse.id,
						weekIndex: ctx.loadedDay.weekIndex,
						dayNo: ctx.loadedDay.dayNo,
						source: "scheduled",
					});
		facts.set(nurse.id, factsOf(nurse, ctx, staffingDate, aroundDay));
		const exclusion = exclusionOf(nurse, ctx, staffingDate, aroundDay);
		if (exclusion === null) {
			eligible.push(nurse);
			continue;
		}
		const list = byFilter.get(exclusion.filter);
		if (list === undefined) byFilter.set(exclusion.filter, [exclusion.person]);
		else list.push(exclusion.person);
	}
	const groups = FILTER_ORDER.flatMap((filter): OutreachExcludedGroup[] => {
		const people = byFilter.get(filter);
		if (people === undefined) return [];
		return [
			{
				filter,
				canOverride: filter === OutreachExclusionFilter.DailyOvertime,
				limitHours: filter === OutreachExclusionFilter.OverMaxDailyHours ? ctx.longShiftHours : null,
				people,
			},
		];
	});
	return { eligible, groups, facts };
};
